import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';

import { useLeaseDetailsDateLinking } from '../../lease-details-date-linking.context';
import { formatStoredDate } from '../utils';

/**
 * Opt-in (`clearWhenDisabled`, e.g. renewal's lease end date on month-to-month). Acts only on the
 * disabled↔enabled transition (not mount / manual clears): disable → clear the value; re-enable →
 * re-derive the linked end date from the start (+leaseDurationMonths −1d, same as the auto-fill).
 */
export const useClearDateWhenDisabled = (path: string, disabled: boolean, enabled: boolean) => {
  const { getValues, setValue } = useFormContext();
  const linkedDates = useLeaseDetailsDateLinking();
  const dateLinkingConfig = linkedDates?.config;
  const isLinkedEndDate = path === dateLinkingConfig?.endField;
  const prevDisabledRef = useRef(disabled);

  useEffect(() => {
    const wasDisabled = prevDisabledRef.current;
    if (wasDisabled === disabled) {
      return;
    }
    prevDisabledRef.current = disabled;
    if (!enabled) {
      return;
    }

    const onBecameDisabled = () => {
      if (getValues(path)) {
        setValue(path, '', { shouldValidate: true, shouldDirty: true });
      }
      if (isLinkedEndDate && linkedDates) {
        linkedDates.endDateManuallyEdited.current = false;
      }
    };

    const onBecameEnabled = () => {
      if (!isLinkedEndDate || !dateLinkingConfig?.startField) {
        return;
      }
      const startRaw = getValues(dateLinkingConfig.startField) as string | undefined;
      const start = startRaw ? dayjs(startRaw) : null;
      if (!start?.isValid()) {
        return;
      }
      const months = dateLinkingConfig.leaseDurationMonths ?? 12;
      const derived = start.add(months, 'month').subtract(1, 'day');
      setValue(path, formatStoredDate(derived, dateLinkingConfig.valueFormat), {
        shouldValidate: true,
        shouldDirty: true,
      });
    };

    if (disabled) {
      onBecameDisabled();
    } else {
      onBecameEnabled();
    }
  }, [
    disabled,
    enabled,
    path,
    setValue,
    getValues,
    isLinkedEndDate,
    linkedDates,
    dateLinkingConfig,
  ]);
};
